import { Box } from '@mantine/core';
import { ReactNode, useEffect, useRef, useState } from 'react';

interface StickyWrapperProps {
  children: ReactNode;
  observeElementId: string;
  topOffset?: number;
  className?: string;
}

export default function StickyWrapper({
  children,
  observeElementId,
  topOffset = 20,
  className,
}: StickyWrapperProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [wrapperWidth, setWrapperWidth] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    const handleScroll = () => {
      if (!wrapperRef.current) return;

      // Store wrapper width on first measurement
      if (!wrapperWidth && wrapperRef.current.offsetWidth) {
        setWrapperWidth(wrapperRef.current.offsetWidth);
      }

      // Find the element to observe
      const observedElement = document.getElementById(observeElementId);
      if (!observedElement) return;

      // Check if observed element content exceeds viewport height
      const observedElementHeight = observedElement.scrollHeight;
      const contentExceedsViewport = observedElementHeight > window.innerHeight;

      // Only make sticky if observed element content is taller than viewport
      if (contentExceedsViewport) {
        const scrollY = window.scrollY;
        const viewportHeight = window.innerHeight;
        const wrapperHeight = wrapperRef.current.offsetHeight;

        // Make sticky when scrolled down enough
        const shouldBeSticky =
          scrollY > 0 &&
          // Ensure wrapper can fit in viewport (prevent bouncing when wrapper is taller than viewport)
          (wrapperHeight < viewportHeight ||
            scrollY > wrapperHeight - viewportHeight);

        setIsSticky(shouldBeSticky);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [wrapperWidth, observeElementId]);

  return (
    <Box
      ref={wrapperRef}
      className={className}
      style={{
        position: isSticky ? 'sticky' : 'static',
        top: isSticky
          ? `calc(var(--app-shell-header-height, 0px) + ${topOffset}px)`
          : 'auto',
        width: isSticky ? `${wrapperWidth}px` : 'auto',
        transition: 'position 0.2s ease',
        zIndex: 10,
      }}
    >
      {children}
    </Box>
  );
}
